// src/core/helpers/htmlSanitizer.ts

/**
 * Convierte saltos de línea de texto plano en <br> tags
 */
const convertLineBreaksToBr = (html: string): string => {
  if (!html || !html.trim()) return html;
  
  // Proteger tags HTML existentes
  const tagPlaceholders: string[] = [];
  let placeholderIndex = 0;
  
  // Reemplazar todos los tags HTML con placeholders temporales
  let processed = html.replace(/<[^>]+>/g, (match) => {
    const placeholder = `___TAG_${placeholderIndex}___`;
    tagPlaceholders[placeholderIndex] = match;
    placeholderIndex++;
    return placeholder;
  });
  
  // Convertir saltos de línea (\n o \r\n) en <br /> tags
  processed = processed.replace(/\r?\n/g, '<br />');
  
  // Restaurar los tags HTML protegidos
  tagPlaceholders.forEach((tag, index) => {
    processed = processed.replace(`___TAG_${index}___`, tag);
  });
  
  return processed;
};

/**
 * Sanitiza HTML de bloques WOD eliminando tags peligrosos y preservando solo formato seguro.
 * Esta función se aplica en la capa de mapeo (parseBlock/parseWod) para sanitizar
 * todo el HTML antes de que llegue a los componentes UI.
 * 
 * Tags permitidos: p, br, ul, ol, li, strong, b, em, i, u, img, iframe, video
 * Tags eliminados: h1-h6, div, span, style, script, meta, link, y todos los demás
 * Atributos eliminados: style, class, id, width, height (excepto src, data-src, alt, title, poster para media)
 * 
 * @param html - HTML crudo del API que puede contener tags peligrosos de editores ricos
 * @returns HTML sanitizado con solo tags seguros y sin atributos peligrosos
 */
export const sanitizeBlockHtml = (html: string): string => {
  if (!html || !html.trim()) return html;
  
  // Lista de tags peligrosos que deben ser eliminados o convertidos
  const dangerousTags = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div', 'span', 'style', 'script', 'meta', 'link', 'head', 'body', 'html'];
  // Tags seguros que preservamos (incluyendo media para que puedan ser extraídos después)
  const safeTags = ['p', 'br', 'ul', 'ol', 'li', 'strong', 'b', 'em', 'i', 'u', 'img', 'iframe', 'video'];
  // Atributos permitidos para tags de media
  const allowedMediaAttribs = ['src', 'data-src', 'alt', 'title', 'poster'];
  
  let processed = html;
  let useDomParser = false;
  
  // Si estamos en el navegador, usar DOMParser para un parsing más robusto
  if (typeof window !== 'undefined' && typeof DOMParser !== 'undefined') {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Verificar si hubo errores de parsing (DOMParser puede fallar silenciosamente)
      const parserError = doc.querySelector('parsererror');
      if (parserError) {
        throw new Error('DOMParser encontró errores en el HTML');
      }
      
      // Función recursiva para sanitizar nodos
      const sanitizeNode = (node: Node): string => {
        if (node.nodeType === Node.TEXT_NODE) {
          return node.textContent || '';
        }
        
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as Element;
          const tagName = element.tagName.toLowerCase();
          
          // Si es un tag peligroso, extraer solo el contenido
          if (dangerousTags.includes(tagName)) {
            // Para h1-h6 y div, convertir a <p> con su contenido
            if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div'].includes(tagName)) {
              const content = Array.from(element.childNodes)
                .map(sanitizeNode)
                .join('');
              return content.trim() ? `<p>${content}</p>` : '';
            }
            // Para span y otros, solo extraer contenido sin wrapper
            if (['span'].includes(tagName)) {
              return Array.from(element.childNodes)
                .map(sanitizeNode)
                .join('');
            }
            // Para style, script, meta, link - eliminar completamente
            return '';
          }
          
          // Si es un tag de media (img, iframe, video), preservarlo pero limpiar atributos peligrosos
          if (['img', 'iframe', 'video'].includes(tagName)) {
            const attrs: string[] = [];
            
            // Preservar solo atributos permitidos
            allowedMediaAttribs.forEach(attrName => {
              const attrValue = element.getAttribute(attrName);
              if (attrValue) {
                // Escapar comillas en valores de atributos
                const escapedValue = attrValue.replace(/"/g, '&quot;');
                attrs.push(`${attrName}="${escapedValue}"`);
              }
            });
            
            const attrsStr = attrs.length > 0 ? ' ' + attrs.join(' ') : '';
            
            // Para img, mantener como self-closing
            if (tagName === 'img') {
              return `<img${attrsStr} />`;
            }
            
            // Para iframe y video, mantener tag con contenido si existe
            const content = Array.from(element.childNodes)
              .map(sanitizeNode)
              .join('');
            
            return content.trim() 
              ? `<${tagName}${attrsStr}>${content}</${tagName}>`
              : `<${tagName}${attrsStr}></${tagName}>`;
          }
          
          // Si es un tag seguro de texto, preservarlo pero limpiar atributos
          if (safeTags.includes(tagName)) {
            const content = Array.from(element.childNodes)
              .map(sanitizeNode)
              .join('');
            
            // Para br, mantenerlo self-closing
            if (tagName === 'br') {
              return '<br />';
            }
            
            // Para otros tags seguros, mantener el tag pero sin atributos
            return `<${tagName}>${content}</${tagName}>`;
          }
          
          // Para cualquier otro tag no reconocido, extraer solo contenido
          return Array.from(element.childNodes)
            .map(sanitizeNode)
            .join('');
        }
        
        return '';
      };
      
      // Sanitizar el body completo
      const bodyContent = Array.from(doc.body.childNodes)
        .map(sanitizeNode)
        .join('');
      
      processed = bodyContent;
      useDomParser = true;
    } catch (e) {
      // Si DOMParser falla, usar fallback regex
      if (process.env.NODE_ENV === 'development') {
        console.warn('DOMParser falló, usando sanitización regex:', e);
      }
      processed = html;
      useDomParser = false;
    }
  }
  
  // Fallback regex-based sanitization (para SSR o cuando DOMParser falla o no está disponible)
  if (!useDomParser) {
    // Convertir h1-h6 a p
    processed = processed.replace(/<\/?h[1-6][^>]*>/gi, (match) => {
      const isClosing = match.startsWith('</');
      return isClosing ? '</p>' : '<p>';
    });
    
    // Eliminar div pero preservar contenido
    processed = processed.replace(/<div[^>]*>/gi, '');
    processed = processed.replace(/<\/div>/gi, '');
    
    // Eliminar span pero preservar contenido
    processed = processed.replace(/<span[^>]*>/gi, '');
    processed = processed.replace(/<\/span>/gi, '');
    
    // Eliminar tags peligrosos completamente
    processed = processed.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
    processed = processed.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
    processed = processed.replace(/<meta[^>]*>/gi, '');
    processed = processed.replace(/<link[^>]*>/gi, '');
    processed = processed.replace(/<\/?head[^>]*>/gi, '');
    processed = processed.replace(/<\/?body[^>]*>/gi, '');
    processed = processed.replace(/<\/?html[^>]*>/gi, '');
    
    // Para tags de media (img, iframe, video), preservar pero limpiar atributos
    // img: preservar src, data-src, alt, title
    processed = processed.replace(/<img([^>]*)>/gi, (match, attrs) => {
      const srcMatch = attrs.match(/(?:src|data-src)=["']([^"']+)["']/i);
      const altMatch = attrs.match(/alt=["']([^"']*)["']/i);
      const titleMatch = attrs.match(/title=["']([^"']*)["']/i);
      
      const preservedAttrs: string[] = [];
      if (srcMatch) {
        preservedAttrs.push(`src="${srcMatch[1]}"`);
      } else {
        const dataSrcMatch = attrs.match(/data-src=["']([^"']+)["']/i);
        if (dataSrcMatch) {
          preservedAttrs.push(`data-src="${dataSrcMatch[1]}"`);
        }
      }
      if (altMatch) preservedAttrs.push(`alt="${altMatch[1]}"`);
      if (titleMatch) preservedAttrs.push(`title="${titleMatch[1]}"`);
      
      const attrsStr = preservedAttrs.length > 0 ? ' ' + preservedAttrs.join(' ') : '';
      return `<img${attrsStr} />`;
    });
    
    // iframe: preservar src, data-src, title
    processed = processed.replace(/<iframe([^>]*)>([\s\S]*?)<\/iframe>/gi, (match, attrs, content) => {
      const srcMatch = attrs.match(/(?:src|data-src)=["']([^"']+)["']/i);
      const titleMatch = attrs.match(/title=["']([^"']*)["']/i);
      
      const preservedAttrs: string[] = [];
      if (srcMatch) {
        preservedAttrs.push(`src="${srcMatch[1]}"`);
      } else {
        const dataSrcMatch = attrs.match(/data-src=["']([^"']+)["']/i);
        if (dataSrcMatch) {
          preservedAttrs.push(`data-src="${dataSrcMatch[1]}"`);
        }
      }
      if (titleMatch) preservedAttrs.push(`title="${titleMatch[1]}"`);
      
      const attrsStr = preservedAttrs.length > 0 ? ' ' + preservedAttrs.join(' ') : '';
      return `<iframe${attrsStr}>${content}</iframe>`;
    });
    
    // video: preservar src, data-src, poster
    processed = processed.replace(/<video([^>]*)>([\s\S]*?)<\/video>/gi, (match, attrs, content) => {
      const srcMatch = attrs.match(/(?:src|data-src)=["']([^"']+)["']/i);
      const posterMatch = attrs.match(/poster=["']([^"']+)["']/i);
      
      const preservedAttrs: string[] = [];
      if (srcMatch) {
        preservedAttrs.push(`src="${srcMatch[1]}"`);
      } else {
        const dataSrcMatch = attrs.match(/data-src=["']([^"']+)["']/i);
        if (dataSrcMatch) {
          preservedAttrs.push(`data-src="${dataSrcMatch[1]}"`);
        }
      }
      if (posterMatch) preservedAttrs.push(`poster="${posterMatch[1]}"`);
      
      const attrsStr = preservedAttrs.length > 0 ? ' ' + preservedAttrs.join(' ') : '';
      return `<video${attrsStr}>${content}</video>`;
    });
    
    // Eliminar TODOS los atributos de todos los demás tags (texto)
    processed = processed.replace(/<(\w+)[^>]*>/g, (match, tagName) => {
      const lowerTagName = tagName.toLowerCase();
      // Si es un tag de media, ya fue procesado arriba
      if (['img', 'iframe', 'video'].includes(lowerTagName)) {
        return match; // Mantener como está (ya fue sanitizado)
      }
      // Solo preservar tags seguros de texto, y sin atributos
      if (safeTags.includes(lowerTagName)) {
        if (lowerTagName === 'br') {
          return '<br />';
        }
        return `<${tagName}>`;
      }
      // Si no es un tag seguro, eliminar el tag completo
      return '';
    });
  }
  
  // Normalización final (aplicar siempre, tanto para DOMParser como regex)
  // Normalizar espacios alrededor de tags
  processed = processed.replace(/>\s+</g, '><');
  processed = processed.replace(/\s+</g, '<');
  processed = processed.replace(/>\s+/g, '>');
  
  // Limpiar espacios múltiples (pero preservar espacios significativos)
  processed = processed.replace(/\s{2,}/g, ' ');
  
  // Limpiar tags p vacíos
  processed = processed.replace(/<p[^>]*>\s*<\/p>/gi, '');
  
  // Normalizar múltiples <br /> consecutivos
  processed = processed.replace(/(<br\s*\/?>\s*){3,}/gi, '<br /><br />');
  
  // Trim final
  processed = processed.trim();
  
  return processed;
};

export const sanitizeHtml = (html: string, showMedia: boolean) => {
  if (showMedia) return html;
  return html
    .replace(/<div[^>]*?(youtube-embed-wrapper)[^>]*?>[\s\S]*?<\/div>/gi, '')
    .replace(/<iframe[\s\S]*?<\/iframe>/gi, '')
    .replace(/<video[\s\S]*?<\/video>/gi, '')
    .replace(/<img[^>]*>/gi, '');
};

export const removeEmptyTags = (htmlStr: string) => {
  // PRIMERO convertir saltos de línea en <br> antes de limpiar tags vacíos
  let processed = convertLineBreaksToBr(htmlStr);
  
  // Luego limpiar tags vacíos (pero preservar los <br> que acabamos de crear)
  processed = processed
    .replace(/<p[^>]*>(\s|&nbsp;|\u00A0|\r|\n)*<\/p>/gi, '')
    .replace(/<div[^>]*>(\s|&nbsp;|\u00A0|\r|\n)*<\/div>/gi, '')
    .replace(/<span[^>]*>(\s|&nbsp;|\u00A0|\r|\n)*<\/span>/gi, '')
    .replace(/(<br\s*\/?>\s*){2,}/gi, '<br />')
    .replace(/^\s+|\s+$/g, '');
  
  return processed;
};
